<template>
    <v-card class="round-card background-2 mb-4">
        <!-- Banner sincronizzazione disabilitata -->
        <v-alert
            v-if="account.mail_sync_enabled === false"
            type="warning"
            text
            tile
            dense
            class="mb-0"
        >
            <div class="d-flex align-center flex-wrap">
                <div class="flex-grow-1">
                    <div class="font-weight-medium">Sincronizzazione email disabilitata</div>
                    <div class="caption">
                        Il collegamento con {{ nomeProvider }} non è più valido (spesso dopo un cambio password o una revoca dell'accesso).
                        Clicca <strong>Riconnetti</strong> per rifare il login con la stessa casella e
                        mantenere lo storico dei ticket collegati.
                    </div>
                </div>
                <v-btn
                    small
                    color="warning"
                    class="ml-2"
                    @click.stop="$emit('reconnect', account)"
                >
                    <v-icon left small>{{ iconaProvider }}</v-icon>
                    Riconnetti
                </v-btn>
            </div>
        </v-alert>

        <!-- Header cliccabile per espandere -->
        <v-card-title
            class="pa-3 cursor-pointer"
            @click="expanded = !expanded"
        >
            <v-avatar size="40" :color="account.mail_sync_enabled === false ? 'warning' : 'primary'" class="mr-3">
                <v-icon dark>{{ account.mail_sync_enabled === false ? 'mdi-email-off' : 'mdi-email' }}</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
                <div class="subtitle-1 font-weight-medium">
                    {{ account.display_name || account.name }}
                    <v-chip x-small label outlined class="ml-2">
                        <v-icon x-small left>{{ iconaProvider }}</v-icon>
                        {{ nomeProvider }}
                    </v-chip>
                </div>
                <div class="caption grey--text">{{ account.name }}</div>
            </div>
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        @click.stop="forceSync"
                        :loading="syncing"
                        :disabled="account.mail_sync_enabled === false"
                        v-bind="attrs"
                        v-on="on"
                    >
                        <v-icon small>mdi-sync</v-icon>
                    </v-btn>
                </template>
                <span>Forza sincronizzazione</span>
            </v-tooltip>
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        color="error"
                        @click.stop="$emit('remove', account.id)"
                        v-bind="attrs"
                        v-on="on"
                    >
                        <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                </template>
                <span>Rimuovi account</span>
            </v-tooltip>
            <v-icon class="ml-2">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-card-title>

        <v-expand-transition>
            <div v-show="expanded">
                <v-divider></v-divider>
                <v-card-text>
                    <!-- Sezione Template Condivisi -->
                    <div class="subtitle-2 mb-3">
                        <v-icon small color="primary" class="mr-1">mdi-file-document-multiple</v-icon>
                        Template Condivisi
                    </div>
                    <v-alert type="info" text dense class="mb-3">
                        <div class="caption">
                            Seleziona i template disponibili per questo account.
                            Solo questi template saranno selezionabili nelle impostazioni sotto.
                        </div>
                    </v-alert>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="localSettings.trouble_templates"
                                :items="troubleTemplates"
                                item-text="name"
                                item-value="id"
                                label="Template Ticket"
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                outlined
                                dense
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="success">mdi-ticket</v-icon>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="localSettings.intervention_templates"
                                :items="interventionTemplates"
                                item-text="name"
                                item-value="id"
                                label="Template Intervento"
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                outlined
                                dense
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="primary">mdi-wrench</v-icon>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                    <v-divider class="mb-4"></v-divider>

                    <!-- Sezione Impostazioni -->
                    <div class="subtitle-2 mb-3">
                        <v-icon small color="primary" class="mr-1">mdi-cog</v-icon>
                        Impostazioni Account
                    </div>

                    <v-row>
                        <!-- Fallback Company -->
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="localSettings.fallback_company_id"
                                :items="companies"
                                item-text="text"
                                item-value="value"
                                label="Cliente di default"
                                placeholder="Cerca cliente..."
                                outlined
                                clearable
                                dense
                                hint="Cliente assegnato quando il mittente non viene riconosciuto"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            ></v-autocomplete>
                        </v-col>

                        <!-- Default User -->
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="localSettings.default_user_id"
                                :items="users"
                                item-text="text"
                                item-value="value"
                                label="Utente di default"
                                placeholder="Cerca utente..."
                                outlined
                                clearable
                                dense
                                hint="Utente assegnato ai ticket creati da email"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:item="{ item }">
                                    <v-list-item-avatar size="32">
                                        <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                        <v-icon v-else>mdi-account-circle</v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Notification Users -->
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="localSettings.notification_user_ids"
                                :items="users"
                                item-text="text"
                                item-value="value"
                                label="Utenti/Gruppi da notificare"
                                placeholder="Cerca utenti o gruppi..."
                                outlined
                                clearable
                                dense
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                hint="Utenti e gruppi che ricevono la notifica quando un ticket viene creato da email"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="info">mdi-bell</v-icon>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-avatar size="32">
                                        <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                        <v-icon v-else>mdi-account-circle</v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Ignored Emails (blacklist apertura ticket) -->
                        <v-col cols="12">
                            <v-combobox
                                v-model="localSettings.ignored_emails"
                                label="Email da ignorare in apertura ticket"
                                placeholder="Premi invio per aggiungere un mittente"
                                outlined
                                dense
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                clearable
                                hint="Email esatte (es. noreply@dominio.it) o wildcard di dominio (es. *@dominio.it). Le email che corrispondono non genereranno ticket."
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="warning">mdi-email-off</v-icon>
                                </template>
                            </v-combobox>
                        </v-col>

                        <!-- No-reply Emails (apre il ticket ma non invia mail automatiche) -->
                        <v-col cols="12">
                            <v-combobox
                                v-model="localSettings.no_reply_emails"
                                label="Email a cui non rispondere"
                                placeholder="Premi invio per aggiungere un mittente"
                                outlined
                                dense
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                clearable
                                hint="Email esatte (es. noreply@dominio.it), wildcard di dominio (es. *@dominio.it) o catch-all (*) per tutti i mittenti. Il ticket viene aperto ma non verrà inviata alcuna mail automatica al mittente."
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="grey">mdi-email-lock</v-icon>
                                </template>
                            </v-combobox>
                        </v-col>

                        <!-- VAT -->
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                :items="vats"
                                v-model="localSettings.vat"
                                label="IVA di Default"
                                clearable
                                dense
                                outlined
                                item-text="text"
                                item-value="value"
                                :menu-props="{'offset-y': true}"
                            ></v-autocomplete>
                        </v-col>

                        <!-- Payment Term -->
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                :items="paymentTerms"
                                v-model="localSettings.payment_term"
                                label="Condizioni Pagamento"
                                clearable
                                dense
                                outlined
                                item-text="text"
                                item-value="value"
                                :menu-props="{'offset-y': true}"
                            ></v-autocomplete>
                        </v-col>

                        <!-- Layout Group -->
                        <v-col cols="12" md="4">
                            <v-select
                                :items="layoutgroups"
                                v-model="localSettings.layoutgroup_id"
                                label="Gruppo Layout"
                                clearable
                                dense
                                outlined
                                item-text="text"
                                item-value="value"
                                :menu-props="{'offset-y': true}"
                            ></v-select>
                        </v-col>
                        <!-- Default Priority -->
                        <v-col cols="12" md="6">
                            <v-select
                                :items="priorities"
                                v-model="localSettings.default_priority"
                                label="Priorità di default"
                                clearable
                                dense
                                outlined
                                item-text="text"
                                item-value="value"
                                hint="Priorità assegnata ai ticket creati da email (se non rilevata dall'oggetto)"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            ></v-select>
                        </v-col>

                        <!-- Default Request Type -->
                        <v-col cols="12" md="6">
                            <v-select
                                :items="requestTypes"
                                v-model="localSettings.default_request_type"
                                label="Tipo richiesta di default"
                                clearable
                                dense
                                outlined
                                item-text="text"
                                item-value="value"
                                hint="Tipo richiesta assegnato ai ticket creati da email"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            ></v-select>
                        </v-col>

                        <!-- Default Sectional -->
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                :items="sectionals"
                                v-model="localSettings.default_sectional_id"
                                label="Sezionale di default"
                                clearable
                                dense
                                outlined
                                item-text="text"
                                item-value="value"
                                hint="Usato per i ticket aperti da email se la company non ha un sezionale ereditato"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            ></v-autocomplete>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Notifica Chiusura Delivery -->
                    <div class="subtitle-2 mb-3">
                        <v-icon small color="primary" class="mr-1">mdi-bell-ring</v-icon>
                        Notifica Chiusura Delivery
                    </div>

                    <v-row align="center">
                        <v-col cols="12" md="6">
                            <v-switch
                                v-model="localSettings.delivery_close_notify_enabled"
                                label="Invia notifica alla chiusura di ticket Delivery"
                                inset
                                hide-details
                                class="mt-0"
                            ></v-switch>
                        </v-col>
                        <v-col cols="12" md="6" v-if="localSettings.delivery_close_notify_enabled">
                            <v-autocomplete
                                :items="users"
                                v-model="localSettings.delivery_close_notify_user_id"
                                item-text="text"
                                item-value="value"
                                label="Utente destinatario"
                                placeholder="Cerca utente..."
                                clearable
                                dense
                                outlined
                                hint="Utente che riceverà la notifica quando un ticket delivery viene chiuso"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:item="{ item }">
                                    <v-list-item-avatar size="32">
                                        <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                        <v-icon v-else>mdi-account-circle</v-icon>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Email libere da notificare alla chiusura delivery -->
                        <v-col cols="12" v-if="localSettings.delivery_close_notify_enabled">
                            <v-combobox
                                v-model="localSettings.delivery_close_notify_emails"
                                label="Email aggiuntive da notificare"
                                placeholder="Premi invio per aggiungere un indirizzo"
                                outlined
                                dense
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                clearable
                                hint="Indirizzi email liberi (es. cliente@dominio.it) che riceveranno una mail quando un ticket delivery viene chiuso"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="info">mdi-email-fast</v-icon>
                                </template>
                            </v-combobox>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Sezione Esportazione Documenti -->
                    <div class="subtitle-2 mb-3">
                        <v-icon small color="primary" class="mr-1">mdi-file-export</v-icon>
                        Esportazione Documenti
                    </div>

                    <v-row>
                        <!-- Prodotto per ore intervento -->
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="localSettings.productcode_hours"
                                :items="productsWithCode"
                                item-text="subtitle"
                                item-value="code"
                                label="Prodotto per ore intervento"
                                placeholder="Cerca prodotto..."
                                outlined
                                clearable
                                dense
                                hint="Codice prodotto usato per le ore di lavoro nella generazione documenti"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="primary">mdi-clock-outline</v-icon>
                                </template>
                                <template v-slot:selection="data">
                                    <v-chip
                                        v-if="data.item && typeof data.item === 'object'"
                                        v-bind="data.attrs"
                                        :input-value="data.selected"
                                        small
                                        pill
                                        @click="data.select"
                                    >
                                        <span class="string-cutter">
                                            {{ data.item.code ? '(' + data.item.code + ') ' : '' }}{{ data.item.text }}
                                        </span>
                                    </v-chip>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-content v-if="typeof item === 'object'">
                                        <v-list-item-title>{{ item.code }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.text }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Prodotto per diritto fisso di chiamata -->
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="localSettings.productcode_fixedvisitcost"
                                :items="productsWithCode"
                                item-text="subtitle"
                                item-value="code"
                                label="Prodotto per diritto fisso di chiamata"
                                placeholder="Cerca prodotto..."
                                outlined
                                clearable
                                dense
                                hint="Codice prodotto usato per il DFC nella generazione documenti"
                                persistent-hint
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="primary">mdi-cash-register</v-icon>
                                </template>
                                <template v-slot:selection="data">
                                    <v-chip
                                        v-if="data.item && typeof data.item === 'object'"
                                        v-bind="data.attrs"
                                        :input-value="data.selected"
                                        small
                                        pill
                                        @click="data.select"
                                    >
                                        <span class="string-cutter">
                                            {{ data.item.code ? '(' + data.item.code + ') ' : '' }}{{ data.item.text }}
                                        </span>
                                    </v-chip>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-content v-if="typeof item === 'object'">
                                        <v-list-item-title>{{ item.code }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ item.text }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <!-- Template Email Automatici -->
                    <div class="subtitle-2 mb-3">
                        <v-icon small color="primary" class="mr-1">mdi-email-fast</v-icon>
                        Template Email Automatici
                    </div>

                    <v-row>
                        <v-col cols="12" md="4">
                            <v-select
                                :items="associatedTroubleTemplates"
                                v-model="localSettings.ticket_opened"
                                label="Apertura Ticket"
                                clearable
                                dense
                                outlined
                                item-text="name"
                                item-value="id"
                                hint="Email inviata quando si apre un ticket"
                                persistent-hint
                                :disabled="!associatedTroubleTemplates.length"
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="success">mdi-ticket-confirmation</v-icon>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                :items="associatedInterventionTemplates"
                                v-model="localSettings.intervention_created"
                                label="Inserimento Intervento"
                                clearable
                                dense
                                outlined
                                item-text="name"
                                item-value="id"
                                hint="Email inviata quando si inserisce un intervento"
                                persistent-hint
                                :disabled="!associatedInterventionTemplates.length"
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="primary">mdi-wrench</v-icon>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                :items="associatedTroubleTemplates"
                                v-model="localSettings.ticket_closed"
                                label="Chiusura Ticket"
                                clearable
                                dense
                                outlined
                                item-text="name"
                                item-value="id"
                                hint="Email inviata quando si chiude il ticket"
                                persistent-hint
                                :disabled="!associatedTroubleTemplates.length"
                                :menu-props="{'offset-y': true}"
                            >
                                <template v-slot:prepend>
                                    <v-icon color="error">mdi-ticket-outline</v-icon>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                    <v-alert
                        v-if="!associatedTroubleTemplates.length && !associatedInterventionTemplates.length"
                        type="warning"
                        text
                        dense
                        class="mt-3"
                    >
                        <div class="caption">
                            Nessun template associato. Seleziona almeno un template nella sezione "Template Condivisi" sopra.
                        </div>
                    </v-alert>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        color="primary"
                        @click="save"
                        :loading="saving"
                    >
                        <v-icon left>mdi-content-save</v-icon>
                        Salva Impostazioni
                    </v-btn>
                </v-card-actions>
            </div>
        </v-expand-transition>
    </v-card>
</template>

<script>
export default {
    name: 'HelpdeskAccountCard',

    props: {
        account: {
            type: Object,
            required: true
        },
        options: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            expanded: false,
            saving: false,
            syncing: false,
            localSettings: {
                trouble_templates: [],
                intervention_templates: [],
                fallback_company_id: null,
                default_user_id: null,
                notification_user_ids: [],
                vat: null,
                payment_term: null,
                layoutgroup_id: null,
                ticket_opened: null,
                intervention_created: null,
                ticket_closed: null,
                productcode_hours: null,
                productcode_fixedvisitcost: null,
                default_priority: null,
                default_request_type: null,
                default_sectional_id: null,
                delivery_close_notify_enabled: false,
                delivery_close_notify_user_id: null,
                delivery_close_notify_emails: [],
                ignored_emails: [],
                no_reply_emails: []
            }
        }
    },

    computed: {
        nomeProvider() {
            return this.account.provider === 'microsoft' ? 'Microsoft 365' : 'Google'
        },

        iconaProvider() {
            return this.account.provider === 'microsoft' ? 'mdi-microsoft' : 'mdi-google'
        },

        troubleTemplates() {
            return this.options.troubleTemplates || []
        },
        interventionTemplates() {
            return this.options.interventionTemplates || []
        },
        vats() {
            return this.options.vats || []
        },
        paymentTerms() {
            return this.options.paymentTerms || []
        },
        layoutgroups() {
            return this.options.layoutgroups || []
        },
        sectionals() {
            return this.options.sectionals || []
        },
        priorities() {
            return this.options.priorities || []
        },
        requestTypes() {
            return this.options.requestTypes || []
        },
        products() {
            return this.options.products || []
        },
        productsWithCode() {
            return this.products.filter(p => p.code)
        },
        companies() {
            return this.options.companies || []
        },
        users() {
            return this.options.users || []
        },

        associatedTroubleTemplates() {
            if (!this.localSettings.trouble_templates || !this.localSettings.trouble_templates.length) {
                return []
            }
            return this.troubleTemplates.filter(t => this.localSettings.trouble_templates.includes(t.id))
        },

        associatedInterventionTemplates() {
            if (!this.localSettings.intervention_templates || !this.localSettings.intervention_templates.length) {
                return []
            }
            return this.interventionTemplates.filter(t => this.localSettings.intervention_templates.includes(t.id))
        },

    },

    watch: {
        account: {
            handler() {
                this.initLocalSettings()
            },
            immediate: true,
            deep: true
        }
    },

    methods: {
        initLocalSettings() {
            this.localSettings = {
                trouble_templates: this.account.trouble_templates || [],
                intervention_templates: this.account.intervention_templates || [],
                fallback_company_id: this.account.fallback_company_id || null,
                default_user_id: this.account.default_user_id || null,
                notification_user_ids: this.account.notification_user_ids || [],
                vat: this.account.vat || null,
                payment_term: this.account.payment_term || null,
                layoutgroup_id: this.account.layoutgroup_id || null,
                ticket_opened: this.account.ticket_opened || null,
                intervention_created: this.account.intervention_created || null,
                ticket_closed: this.account.ticket_closed || null,
                productcode_hours: this.account.productcode_hours || null,
                productcode_fixedvisitcost: this.account.productcode_fixedvisitcost || null,
                default_priority: this.account.default_priority || null,
                default_request_type: this.account.default_request_type || null,
                default_sectional_id: this.account.default_sectional_id || null,
                delivery_close_notify_enabled: this.account.delivery_close_notify_enabled || false,
                delivery_close_notify_user_id: this.account.delivery_close_notify_user_id || null,
                delivery_close_notify_emails: this.normalizeEmails(this.account.delivery_close_notify_emails),
                ignored_emails: Array.isArray(this.account.ignored_emails) ? this.account.ignored_emails : [],
                no_reply_emails: Array.isArray(this.account.no_reply_emails) ? this.account.no_reply_emails : []
            }
        },

        normalizeEmails(list) {
            if (!Array.isArray(list)) return []
            const seen = new Set()
            const result = []
            for (const raw of list) {
                if (typeof raw !== 'string') continue
                const clean = raw.trim().toLowerCase()
                if (!clean || seen.has(clean)) continue
                seen.add(clean)
                result.push(clean)
            }
            return result
        },

        save() {
            this.saving = true
            // Normalizza gli indirizzi liberi prima dell'invio: trim, lowercase, dedup, no vuoti.
            this.localSettings.delivery_close_notify_emails = this.normalizeEmails(this.localSettings.delivery_close_notify_emails)
            this.axios.post('save-helpdesk-settings', {
                google_account_id: this.account.id,
                ...this.localSettings
            })
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Impostazioni salvate',
                        color: 'success',
                        timeout: 3000
                    })
                    this.$emit('saved')
                })
                .catch(error => {
                    console.error('Error saving settings:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore durante il salvataggio',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.saving = false
                })
        },

        forceSync() {
            this.syncing = true
            this.axios.post(`google/${this.account.id}/mail-sync`)
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Sincronizzazione avviata',
                        color: 'success',
                        timeout: 3000
                    })
                })
                .catch(error => {
                    console.error('Error syncing:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore durante la sincronizzazione',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.syncing = false
                })
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>
